import {XIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {ForYouItem} from '../shared/ForYouItem'
import {useMergeabilityData, type MergeabilityData} from './use-mergeability-data'
import {OverviewMergeControl} from './shared/OverviewMergeControl'
import {useForYouActionErrors} from '../shared/use-for-you-action-errors'

// Entry statuses that mean the entry itself has an unresolved problem, rather than
// merely being blocked by another entry further down the stack.
const BLOCKING_ENTRY_STATUSES = new Set(['NOT_READY', 'DRAFT', 'CLOSED', 'MERGE_CONFLICTS', 'NEEDS_REBASE'])

// `stackEntries` is ordered top (upstack) to base (downstack); the last entry with
// an unresolved status is the one closest to the base of the stack.
function findBlockingDownstackPullRequest(data: MergeabilityData | null) {
  const entries = data?.stackSection?.stackEntries
  if (!entries) return undefined

  const currentIndex = entries.findIndex(entry => entry.position === 'CURRENT')
  const currentStatus = entries[currentIndex]?.effectiveEntryStatus
  if (currentIndex === -1 || !(currentStatus === 'BLOCKED_DOWNSTACK' || currentStatus === 'NOT_READY')) return undefined

  return entries
    .slice(currentIndex + 1)
    .findLast(entry => entry.effectiveEntryStatus && BLOCKING_ENTRY_STATUSES.has(entry.effectiveEntryStatus))?.pull
}

export function shouldShowBlockedByDownstackPullRequest(data: MergeabilityData | null): boolean {
  return (
    !!data &&
    !!data.stackSection?.shouldRender &&
    !data.isClosedOrMerged &&
    !data.mergeBoxPullRequest.isDraft &&
    !!findBlockingDownstackPullRequest(data)
  )
}

// Renders when the only thing keeping this pull request from merging is an
// unresolved problem on a pull request further down the stack.
export function BlockedByDownstackPullRequest() {
  const {onQueueError, footer} = useForYouActionErrors()
  const data = useMergeabilityData()
  const blockingPullRequest = findBlockingDownstackPullRequest(data)

  if (!shouldShowBlockedByDownstackPullRequest(data) || !blockingPullRequest) return null

  return (
    <ForYouItem
      itemId="blocked-by-downstack-pull-request"
      footer={footer}
      visual={{kind: 'icon', icon: XIcon, tone: 'danger', glyphSize: 12}}
      title="Blocked by a downstack pull request"
      subText={
        <>
          Resolve the issues in{' '}
          <Link inline href={blockingPullRequest.url} className="Link--inTextBlock">
            #{blockingPullRequest.number}
          </Link>{' '}
          before merging this pull request.
        </>
      }
      actions={[
        {
          id: 'merge-control',
          content: (
            <OverviewMergeControl
              data={data}
              itemName="blocked-by-downstack-pull-request"
              fallbackAction="view-status"
              onQueueError={onQueueError}
            />
          ),
        },
      ]}
    />
  )
}
